import { useCallback } from 'react'

import type { ConnectError } from '@connectrpc/connect'
import type { UseMutationResult } from '@tanstack/react-query'

import { useWorkflowCaseIdParam } from '~/hooks/workflowCase/useWorkflowCaseIdParam'

/**
 * 入札の書き込み mutation に操作元の業務ワークフローケース ID (`?workflowId=`) を載せる。
 * backend は完了済み / 削除済みのケース経由の書き込みを拒否する
 * (AC-BIN-050 / AC-BSP-058 / AC-BSP-079、 AC-WFW-014 の backend 側の enforce)。
 * call site が多く、 1 箇所でも積み忘れるとその経路だけゲートを素通りするため、
 * 呼び出し元ではなく mutation hook 側で積む。
 */
export function useWorkflowCaseScopedMutation<
  TData,
  TVariables extends { caseId?: string },
>(
  mutation: UseMutationResult<TData, ConnectError, TVariables>,
): UseMutationResult<TData, ConnectError, TVariables> {
  const caseId = useWorkflowCaseIdParam()
  const { mutate, mutateAsync } = mutation

  const scopedMutate = useCallback<typeof mutate>(
    (variables, options) => {
      mutate(withCaseId(variables, caseId), options)
    },
    [mutate, caseId],
  )
  const scopedMutateAsync = useCallback<typeof mutateAsync>(
    (variables, options) => mutateAsync(withCaseId(variables, caseId), options),
    [mutateAsync, caseId],
  )

  if (caseId === undefined) return mutation
  return { ...mutation, mutate: scopedMutate, mutateAsync: scopedMutateAsync }
}

function withCaseId<TVariables extends { caseId?: string }>(
  variables: TVariables,
  caseId: string | undefined,
): TVariables {
  if (caseId === undefined) return variables
  // spread の結果は TVariables に戻らないため cast する。 caseId? は型制約で保証済み。
  return { ...variables, caseId } as TVariables
}
